import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Select, FormControl,
  InputLabel, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, CircularProgress, Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  CleaningServices as CleanIcon,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

const TARGET_TYPES = [
  { value: 'all', label: 'All Students', icon: <PeopleIcon fontSize="small" />, color: '#2196f3' },
  { value: 'filiere', label: 'By Filière', icon: <SchoolIcon fontSize="small" />, color: '#ff9800' },
  { value: 'student', label: 'Single Student (Apogée)', icon: <PersonIcon fontSize="small" />, color: '#9c27b0' },
];

export default function NotificationsManagement() {
  const [notifications, setNotifications] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm]   = useState(null);
  const [cleanConfirm, setCleanConfirm]     = useState(null); // { type, label, days? }
  const [cleaning, setCleaning]             = useState(false);

  const [form, setForm] = useState({
    title: '',
    message: '',
    target_type: 'all',
    target_value: '',
    expires_at: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [notifs, fils] = await Promise.all([
        adminAPI.getNotificationsList(),
        adminAPI.getNotificationFilieres(),
      ]);
      setNotifications(Array.isArray(notifs) ? notifs : []);
      setFilieres(Array.isArray(fils) ? fils : []);
    } catch (e) {
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSend = async () => {
    if (!form.title.trim() || !form.message.trim()) {
      setError('Title and message are required');
      return;
    }
    if ((form.target_type === 'filiere' || form.target_type === 'student') && !form.target_value.trim()) {
      setError('Please specify the target value');
      return;
    }
    setSending(true);
    setError('');
    try {
      await adminAPI.createNotification({
        title: form.title,
        message: form.message,
        target_type: form.target_type,
        target_value: form.target_type === 'all' ? null : form.target_value,
        expires_at: form.expires_at || null,
      });
      setSuccess('Notification sent successfully');
      setDialogOpen(false);
      setForm({ title: '', message: '', target_type: 'all', target_value: '', expires_at: '' });
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminAPI.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setDeleteConfirm(null);
    } catch {
      setError('Failed to delete notification');
    }
  };

  const handleClean = async () => {
    if (!cleanConfirm) return;
    setCleaning(true);
    try {
      let result;
      if (cleanConfirm.type === 'expired') result = await adminAPI.deleteExpiredNotifications();
      else if (cleanConfirm.type === 'older')   result = await adminAPI.deleteOldNotifications(cleanConfirm.days);
      else                                       result = await adminAPI.deleteAllNotifications();
      setSuccess(`${result.deleted} notification(s) supprimée(s).`);
      setCleanConfirm(null);
      load();
    } catch {
      setError('Erreur lors du nettoyage.');
    } finally {
      setCleaning(false);
    }
  };

  const targetChip = (n) => {
    const t = TARGET_TYPES.find(x => x.value === n.target_type);
    const label = n.target_type === 'all'
      ? 'All Students'
      : n.target_type === 'filiere'
        ? `Filière: ${n.target_value}`
        : `Apogée: ${n.target_value}`;
    return (
      <Chip
        size="small"
        icon={t?.icon}
        label={label}
        sx={{ bgcolor: `${t?.color}18`, color: t?.color, fontWeight: 600 }}
      />
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsIcon sx={{ color: '#e67e22', fontSize: 30 }} />
          <Typography variant="h5" fontWeight={700}>Notifications Management</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setDialogOpen(true); setError(''); setSuccess(''); }}
          sx={{ background: 'linear-gradient(135deg, #e67e22, #d35400)', borderRadius: 2 }}
        >
          New Notification
        </Button>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Cleanup toolbar */}
      {notifications.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, p: 1.5, bgcolor: '#fff8f0', border: '1px solid #fde8cc', borderRadius: 2, flexWrap: 'wrap' }}>
          <CleanIcon sx={{ color: '#e67e22', fontSize: 20 }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: '#e67e22', mr: 1 }}>
            Nettoyage :
          </Typography>
          <Button size="small" variant="outlined" color="warning"
            onClick={() => setCleanConfirm({ type: 'expired', label: 'Supprimer toutes les notifications expirées' })}>
            Supprimer les expirées
          </Button>
          <Button size="small" variant="outlined" color="warning"
            onClick={() => setCleanConfirm({ type: 'older', days: 30, label: 'Supprimer les notifications de plus de 30 jours' })}>
            &gt; 30 jours
          </Button>
          <Button size="small" variant="outlined" color="warning"
            onClick={() => setCleanConfirm({ type: 'older', days: 7, label: 'Supprimer les notifications de plus de 7 jours' })}>
            &gt; 7 jours
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" variant="contained" color="error" startIcon={<DeleteSweepIcon />}
            onClick={() => setCleanConfirm({ type: 'all', label: 'Supprimer TOUTES les notifications' })}>
            Tout supprimer
          </Button>
        </Box>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 6, color: 'text.secondary' }}>
            <NotificationsIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
            <Typography>No notifications yet</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reads</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Expires</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sent</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>By</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {notifications.map((n) => (
                  <TableRow key={n.id} hover>
                    <TableCell sx={{ fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.title}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                      {n.message}
                    </TableCell>
                    <TableCell>{targetChip(n)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={n.read_count} color="default" />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                      {n.expires_at ? new Date(n.expires_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                      {new Date(n.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{n.created_by_username}</TableCell>
                    <TableCell>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm(n)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsIcon sx={{ color: '#e67e22' }} /> New Notification
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TextField
            label="Title"
            fullWidth
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Message"
            fullWidth
            multiline
            rows={4}
            value={form.message}
            onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Target</InputLabel>
            <Select
              value={form.target_type}
              label="Target"
              onChange={e => setForm(f => ({ ...f, target_type: e.target.value, target_value: '' }))}
            >
              {TARGET_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t.icon} {t.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {form.target_type === 'filiere' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Filière</InputLabel>
              <Select
                value={form.target_value}
                label="Filière"
                onChange={e => setForm(f => ({ ...f, target_value: e.target.value }))}
              >
                {filieres.map(f => (
                  <MenuItem key={f.cod_etp} value={f.cod_etp}>
                    {f.lib_etp} ({f.cod_etp})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {form.target_type === 'student' && (
            <TextField
              label="Student Apogée (cod_etu)"
              fullWidth
              value={form.target_value}
              onChange={e => setForm(f => ({ ...f, target_value: e.target.value }))}
              sx={{ mb: 2 }}
              placeholder="e.g. 20240001"
            />
          )}

          <TextField
            label="Expiry Date (optional)"
            type="datetime-local"
            fullWidth
            value={form.expires_at}
            onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={sending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending}
            sx={{ background: 'linear-gradient(135deg, #e67e22, #d35400)' }}
          >
            {sending ? <CircularProgress size={20} color="inherit" /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete single confirm */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete "<strong>{deleteConfirm?.title}</strong>"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm.id)}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk clean confirm */}
      <Dialog open={!!cleanConfirm} onClose={() => !cleaning && setCleanConfirm(null)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteSweepIcon color="error" /> Confirmer le nettoyage
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1, borderRadius: 2 }}>
            {cleanConfirm?.label}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Cette action est irréversible. Les notifications supprimées ne peuvent pas être récupérées.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanConfirm(null)} disabled={cleaning}>Annuler</Button>
          <Button color="error" variant="contained" onClick={handleClean} disabled={cleaning}
            startIcon={cleaning ? <CircularProgress size={16} color="inherit" /> : <DeleteSweepIcon />}>
            {cleaning ? 'Suppression…' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
